import { useMutation, useQuery } from '@apollo/client';
import {
  GetDocumentOversightResultByIdDocument,
  InsertDocumentOversightResultDocument,
  Parent_Type_Enum,
  UpdateDocumentOversightResultDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { ModalBodyWrapper } from 'src/components/form/form/ModalBodyWrapper';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import DocumentAssessmentResultForm from 'src/pages/assessments/forms/DocumentAssessmentResultForm';
import type { DocumentAssessmentResultFormDataFields } from 'src/pages/assessments/forms/documentAssessmentResultSchema';
import { defaultValues } from 'src/pages/assessments/forms/documentAssessmentResultSchema';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useMultiParentFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { evictField } from '@/utils/graphqlUtils';
import { oversightAssessmentResultsUrl } from '@/utils/urls';

type Props = {
  readonly: boolean;
  navigateToResults: boolean;
  isModalForm: boolean;
  parentId?: string;
  assessedItem?: ObjectWithContributors;
  id?: string;
  onDismiss?: (saved: boolean) => void;
  beforeFieldsSlot?: ReactNode;
  showAssessmentSelector?: boolean;
  documentIds?: string[];
  header?: string;
};

const ConnectedDocumentOversightResultForm: FC<Props> = ({
  readonly,
  parentId,
  assessedItem,
  id,
  onDismiss,
  beforeFieldsSlot,
  showAssessmentSelector,
  navigateToResults,
  isModalForm,
  documentIds,
  header,
}) => {
  const navigate = useNavigate();

  const { data } = useQuery(GetDocumentOversightResultByIdDocument, {
    variables: {
      Id: id!,
    },
    fetchPolicy: 'no-cache',
    skip: !id,
  });
  const documentOversightResult = data?.document_oversight_result?.[0];
  const {
    hasPermission: canUpdateDocumentOversightResult,
    loading: isLoadingCanUpdateDocumentAssessmentResult,
  } = useHasPermissionQuery('update:document_second_line_result', assessedItem);

  const { updateFiles } = useMultiParentFileUpdate();
  const [insertDocumentOversightResult] = useMutation(
    InsertDocumentOversightResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'document_oversight_result');
        evictField(cache, 'oversight_assessment');
        evictField(cache, 'document_oversight_result_aggregate');
      },
    }
  );

  const [updateDocumentOversightResult] = useMutation(
    UpdateDocumentOversightResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'document_oversight_result');
        evictField(cache, 'oversight_assessment');
        evictField(cache, 'document_oversight_result_aggregate');
      },
    }
  );

  documentIds = documentIds ?? [];
  const parentDocument = documentOversightResult?.parents.find(
    (p) => p.document
  );
  if (parentDocument?.document?.Id) {
    documentIds.push(parentDocument.document.Id);
  } else if (assessedItem?.Id) {
    documentIds.push(assessedItem.Id);
  }

  const onSave = async (values: DocumentAssessmentResultFormDataFields) => {
    const { files } = values;
    const documentOversightResultIds: string[] = [];
    if (documentOversightResult) {
      const result = await updateDocumentOversightResult({
        variables: {
          ...values,
          CustomAttributeData: values.CustomAttributeData ?? null,
          Id: documentOversightResult.Id,
        },
      });

      if (result.data?.update_document_oversight_result?.affected_rows === 0) {
        throw new Error('Document second line result update failed');
      }
      documentOversightResultIds.push(id!);
    } else {
      const result = await insertDocumentOversightResult({
        variables: {
          ...values,
          CustomAttributeData: values.CustomAttributeData ?? null,
          OversightAssessmentId: parentId!,
          DocumentIds: values?.DocumentIds?.map((r) => r.value),
        },
      });

      if (!result.data?.insertChildDocumentOversightResult?.Ids) {
        throw new Error('Document second line result id is missing');
      }
      documentOversightResultIds.push(
        ...result.data.insertChildDocumentOversightResult.Ids
      );
    }

    await updateFiles({
      parentIds: documentOversightResultIds,
      parentType: Parent_Type_Enum.DocumentSecondLineResult,
      selectedFiles: files,
      originalFiles: documentOversightResult?.files.map((f) => f.file) ?? [],
    });
    if (navigateToResults && parentId) {
      navigate(oversightAssessmentResultsUrl(parentId));
    }
  };

  return (
    <DocumentAssessmentResultForm
      header={header}
      defaultValues={{
        ...defaultValues,
        DocumentIds: documentIds.map((c) => ({ value: c })),
        OversightAssessmentId: parentId,
      }}
      values={
        documentOversightResult
          ? {
              Rating: documentOversightResult?.Rating ?? 1,
              Rationale: documentOversightResult?.Rationale ?? '',
              OversightAssessmentId: parentId,
              DocumentIds: documentIds.map((c) => ({ value: c })),
              TestDate: documentOversightResult?.TestDate,
              CustomAttributeData: documentOversightResult?.CustomAttributeData,
              files: documentOversightResult?.files.map((f) => f.file) ?? [],
            }
          : undefined
      }
      onDismiss={onDismiss}
      onSave={onSave}
      readOnly={
        readonly &&
        !isLoadingCanUpdateDocumentAssessmentResult &&
        !canUpdateDocumentOversightResult
      }
      renderTemplate={(renderProps) =>
        isModalForm ? (
          <ModalBodyWrapper {...renderProps} />
        ) : (
          <PageWrapper {...renderProps} />
        )
      }
      disableDocumentSelector={id != undefined}
      beforeFieldsSlot={beforeFieldsSlot}
      showSelector={
        showAssessmentSelector ? 'compliance_monitoring_assessment' : undefined
      }
      assessmentMode={'compliance_monitoring_assessment'}
    />
  );
};

export default ConnectedDocumentOversightResultForm;
